'use client';

import { SvgChevronright, SvgHamburger } from '@common/icon';
import { cn } from '@core/utils';
import * as RadixDropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';

interface CategoryItem {
  id: string;
  label: string;
  href?: string;
  children?: CategoryItem[];
}

interface CategoryDropdownProps {
  categories: CategoryItem[];
  className?: string;
}

// 확장형 메뉴 아이템 컴포넌트
const ExpandableMenuItem = ({
  item,
  initialSelectedChild,
  onClose,
}: {
  item: CategoryItem;
  initialSelectedChild?: CategoryItem | null;
  onClose: () => void;
}) => {
  const pathname = usePathname();

  const [selectedChild, setSelectedChild] = useState<CategoryItem | null>(
    initialSelectedChild || null,
  );

  // initialSelectedChild가 변경될 때 selectedChild 업데이트
  useEffect(() => {
    setSelectedChild(initialSelectedChild || null);
  }, [initialSelectedChild]);

  // 자식이 있는 경우 수평 레이아웃으로 표시
  return (
    <div className="flex">
      {/* 왼쪽: 자식 메뉴 아이템들 */}
      <div className="min-w-160 py-12 pl-12">
        {item.children?.map((child) => {
          if (child.href) {
            return (
              <LinkItem
                key={child.id}
                href={child.href}
                label={child.label}
                isSelected={pathname === child.href}
                onClick={onClose}
              />
            );
          }

          return (
            <GroupItem
              key={child.id}
              isSelected={selectedChild?.id === child.id}
              label={child.label}
              onClick={() => {
                if (selectedChild?.id === child.id) {
                  return;
                }

                setSelectedChild(child);
              }}
            />
          );
        })}
      </div>

      {/* 가운데: 세로 divider */}
      {selectedChild && (
        <>
          <div className="mx-16 min-h-100 w-1 self-stretch bg-Gray-200" />

          {/* 오른쪽: 선택된 자식의 하위 메뉴들 */}
          <div className="max-h-[27.375rem] min-w-160 overflow-y-auto py-12 pr-12">
            {selectedChild.children
              ?.filter((grandChild) => grandChild.href)
              .map((grandChild) => (
                <LinkItem
                  isSelected={pathname === grandChild.href}
                  key={grandChild.id}
                  href={grandChild.href!}
                  label={grandChild.label}
                  onClick={onClose}
                />
              ))}
          </div>
        </>
      )}
    </div>
  );
};

// 메인 카테고리 드롭다운 컴포넌트
export const CategoryDropdown = ({
  categories,
  className,
}: CategoryDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialSelectedChild, setInitialSelectedChild] =
    useState<CategoryItem | null>(null);

  return (
    <RadixDropdownMenuPrimitive.Root
      open={isOpen}
      onOpenChange={(opened) => {
        setIsOpen(opened);
        // 드롭다운이 열릴 때 첫 번째 카테고리의 첫 번째 자식을 선택
        if (
          opened &&
          categories.length > 0 &&
          categories[0].children &&
          categories[0].children.length > 0
        ) {
          setInitialSelectedChild(categories[0].children[0]);
        } else if (!opened) {
          setInitialSelectedChild(null);
        }
      }}
    >
      <RadixDropdownMenuPrimitive.Trigger
        className={cn(
          'flex min-h-56 cursor-pointer items-center gap-8 border-none bg-transparent px-24 py-0 outline-none',
          'font-600 text-Gray-800 typo-body2',
          className,
        )}
      >
        <SvgHamburger className="size-24 text-Gray-800" />
        카테고리
      </RadixDropdownMenuPrimitive.Trigger>

      <RadixDropdownMenuPrimitive.Portal>
        <RadixDropdownMenuPrimitive.Content
          className="z-10 min-w-240 overflow-y-auto rounded-b-small border-1 border-Gray-300 bg-Gray-white shadow-xl"
          side="bottom"
          align="start"
          sideOffset={0}
        >
          {categories.map((category, index) => (
            <ExpandableMenuItem
              key={category.id}
              item={category}
              initialSelectedChild={index === 0 ? initialSelectedChild : null}
              onClose={() => setIsOpen(false)}
            />
          ))}
        </RadixDropdownMenuPrimitive.Content>
      </RadixDropdownMenuPrimitive.Portal>
    </RadixDropdownMenuPrimitive.Root>
  );
};

const LinkItem = ({
  isSelected,
  href,
  label,
  onClick,
}: {
  isSelected: boolean;
  href: string;
  label: string;
  onClick?: () => void;
}) => {
  return (
    <CustomLink
      className={cn(
        'flex cursor-pointer items-center justify-between rounded-xsmall px-16 py-12 text-Gray-550 hover:text-Gray-black',
        { 'bg-Gray-100': isSelected },
      )}
      href={href}
      onClick={onClick}
    >
      <span
        className={cn('font-400 text-inherit typo-body3', {
          'text-Gray-black font-700': isSelected,
        })}
      >
        {label}
      </span>
    </CustomLink>
  );
};

const GroupItem = ({
  isSelected,
  label,
  onClick,
}: {
  isSelected: boolean;
  label: string;
  onClick: () => void;
}) => {
  return (
    <div
      className={cn(
        'flex cursor-pointer items-center justify-between rounded-xsmall px-16 py-12 text-Gray-550 hover:text-Gray-black',
        { 'bg-Gray-100': isSelected },
      )}
      onClick={onClick}
    >
      <span
        className={cn('font-400 text-inherit typo-body3', {
          'text-Gray-black font-700': isSelected,
        })}
      >
        {label}
      </span>
      {isSelected && <SvgChevronright className="size-20 text-Gray-black" />}
    </div>
  );
};
